import { useState, useEffect, useMemo } from 'react';
import { IStalls } from '../../../../../Types/RegisterTypes';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import { AiOutlineClose, AiOutlineSearch } from 'react-icons/ai';
import Tab from 'react-bootstrap/Tab';
import Tabs from 'react-bootstrap/Tabs';
import Form from 'react-bootstrap/Form';
import InputGroup from 'react-bootstrap/InputGroup';
import {
  useGetAllFairStallList,
  useGetFairLayoutPdf,
} from '../../../apiEndpoints/exhibitorApiEndpoints';
import { Spinner } from 'react-bootstrap';
import { imgUrl } from '../../../../../Helpers/Constant';

const AddSpaceRequirements = ({
  setStalls,
  stalls,
}: {
  setStalls: React.Dispatch<React.SetStateAction<number[]>>;
  stalls: number[];
}) => {
  const { data, isSuccess, isLoading, isFetching } = useGetAllFairStallList();
  const { data: fairLayout } = useGetFairLayoutPdf();

  const [allStalls, setAllStalls] = useState<IStalls[]>([]);
  const [status, setStatus] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState<string | null>(null);

  // modal state
  const [show, setShow] = useState(false);
  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);

  useEffect(() => {
    if (isSuccess || isFetching) {
      setAllStalls(data?.data || []);
    }
  }, [isSuccess, isFetching]);

  // Group stalls by hall name
  const groupedStalls = useMemo(() => {
    const groups: Record<string, IStalls[]> = {};

    allStalls.forEach((stall) => {
      if (!groups[stall.fair_stall_hall_name]) {
        groups[stall.fair_stall_hall_name] = [];
      }
      groups[stall.fair_stall_hall_name].push(stall);
    });

    return groups;
  }, [allStalls]);

  // Filter stalls based on search term
  const filteredStalls = useMemo(() => {
    if (!searchTerm) return groupedStalls;
    const filtered: Record<string, IStalls[]> = {};
    Object.keys(groupedStalls).forEach((hallName) => {
      const hallStalls = groupedStalls[hallName].filter((stall) =>
        stall.stall_no.toLowerCase().includes(searchTerm?.toLowerCase())
      );

      if (hallStalls?.length > 0) {
        setActiveTab(hallName);
        filtered[hallName] = hallStalls;
      }
    });
    return filtered;
  }, [groupedStalls, searchTerm]);

  const handleStallClick = (stall: IStalls) => {
    const newStatusStall = allStalls.find(
      (s) => s.fair_stall_id === stall.fair_stall_id
    );

    if (!newStatusStall) return;
    if (stall.fair_stall_available_status === 'available') {
      setStalls([...stalls, stall.fair_stall_id]);
      newStatusStall.fair_stall_available_status = 'select';
      setAllStalls([...allStalls]);
    } else if (newStatusStall.fair_stall_available_status === 'select') {
      newStatusStall.fair_stall_available_status = 'available';
      const newStall = stalls.filter((st: any) => st !== stall.fair_stall_id);
      setStalls(newStall);
      setAllStalls([...allStalls]);
    } else {
      handleShow();
      setStatus(stall.fair_stall_available_status);
    }
  };

  return (
    <>
      <div className='register-top'>
        <div className='register-top-div d-flex align-items-center justify-content-between'>
          <h3></h3>
          <h5
            onClick={() => {
              window.open(
                `${imgUrl}/${fairLayout?.data?.fair_venue_layout}`,
                '_blank'
              );
            }}
            className='pointer'
          >
            <img
              src='/assets/venue-layout.jpg'
              alt='Venue layout'
              width={'50px'}
              height={'40px'}
              style={{ objectFit: 'contain' }}
            />

            <span className='venue-layout-color-animation'> Venue layout</span>
          </h5>
        </div>
        <div>
          <div className='mb-3'>
            <Row className='align-items-center justify-content-between mx-2 my-2'>
              {/* Left side: Selected counter + Clear button */}
              <Col xs={12} md={5} lg={4} className='mb-2 mb-md-0'>
                <div className='d-flex align-items-center gap-2'>
                  {stalls?.length > 0 && (
                    <>
                      <div className='selected-stalls-counter p-2 rounded bg-light'>
                        <strong>
                          <span className='text-primary'>Selected: </span>
                          <span className='badge bg-primary'>
                            {stalls.length}
                          </span>
                        </strong>
                      </div>
                      <Button
                        variant='outline-danger'
                        size='sm'
                        onClick={() => {
                          setStalls([]);
                          // Reset all selected stalls to available
                          setAllStalls(
                            allStalls?.map((stall) => ({
                              ...stall,
                              fair_stall_available_status:
                                stall.fair_stall_available_status === 'select'
                                  ? 'available'
                                  : stall.fair_stall_available_status,
                            }))
                          );
                        }}
                        className='ms-2'
                      >
                        Clear All
                      </Button>
                    </>
                  )}
                </div>
              </Col>

              {/* Right side: Search bar */}
              <Col xs={12} md={5}>
                <InputGroup className='shadow-sm'>
                  <InputGroup.Text className='bg-white'>
                    <AiOutlineSearch className='text-secondary' />
                  </InputGroup.Text>
                  <Form.Control
                    type='text'
                    placeholder='Search stalls by name...'
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className='border-start-0'
                  />
                  {searchTerm && (
                    <Button
                      variant='outline-secondary'
                      onClick={() => setSearchTerm('')}
                      className='border-end-0'
                    >
                      <AiOutlineClose />
                    </Button>
                  )}
                </InputGroup>
              </Col>
            </Row>
          </div>
        </div>

        {Object.keys(filteredStalls)?.length === 0 && (
          <div className='text-center py-4'>
            <p>No stalls found matching your search.</p>
          </div>
        )}

        {isLoading ? (
          <div className='d-flex justify-content-center py-2'>
            <Spinner />
          </div>
        ) : (
          <Tabs
            activeKey={activeTab || undefined}
            onSelect={(k) => setActiveTab(k)}
            className='mb-3'
            fill
          >
            {Object?.keys(filteredStalls)?.map((hallName) => (
              <Tab
                key={hallName}
                eventKey={hallName}
                title={`${hallName} Hall`}
              >
                <div className='register-top-form-card space-requirment-div'>
                  <Row>
                    {filteredStalls[hallName]?.map((stall) => (
                      <Col
                        sm={12}
                        md={4}
                        lg={3}
                        xl={3}
                        xxl={2}
                        key={stall.fair_stall_id}
                        className='mb-3 stall-col'
                      >
                        <div
                          onClick={() => handleStallClick(stall)}
                          className={`
      premium-stall
      status-${stall.fair_stall_available_status}
      ${stall.fair_stall_air_conditioned ? 'air-conditioned' : ''}
    `}
                        >
                          <div className='stall-curtain'></div>
                          <div className='stall-number-plate'>
                            <span>{stall.stall_no}</span>
                          </div>

                          <div className='stall-content'>
                            {stall.booked_by && (
                              <div className='booked-by-info'>
                                <div className='booked-by-label'>Booked By</div>
                                <div className='booked-by-name'>
                                  {stall.booked_by}
                                </div>
                              </div>
                            )}

                            <div className='hall-name'>
                              {stall.fair_stall_type}
                            </div>
                            <div className='stall-status'>
                              {stall.fair_stall_available_status === 'select'
                                ? 'Selected'
                                : stall.fair_stall_available_status}
                            </div>
                            {stall.fair_stall_air_conditioned ? (
                              <div className='ac-badge'>
                                <>
                                  <i className='ac-icon'>❄️</i> AC
                                </>
                              </div>
                            ) : (
                              <></>
                            )}
                          </div>
                          <div className='stall-glow'></div>
                        </div>
                      </Col>
                    ))}
                  </Row>
                </div>
              </Tab>
            ))}
          </Tabs>
        )}
      </div>

      <Modal show={show} onHide={handleClose} centered>
        <Modal.Header closeButton>
          <Modal.Title>Stall Status</Modal.Title>
        </Modal.Header>
        <Modal.Body className='text-center fs-5'>
          This stall is currently{' '}
          <span className='text-secondary fw-bold'>{status}</span>.
        </Modal.Body>
        <Modal.Footer>
          <Button variant='secondary' onClick={handleClose}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default AddSpaceRequirements;
